import {
  Anchor,
  Box,
  Button,
  Group,
  SimpleGrid,
  Stack,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { AppShell } from '../components/app-shell/AppShell';
import {
  AvailabilitySummaryCard,
  DashboardGreeting,
  DashboardStatGrid,
  type DashboardStatTile,
  type QuickAction,
  QuickActionRow,
  RecentActivityFeed,
  resolveGreetingPhrase,
  UpNextCard,
} from '../components/dashboard';
import { EmptyState, MotionSection, SectionTitle } from '../components/ui';
import { fetchOk } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { track } from '../lib/telemetry';

/**
 * /dashboard — landing surface after sign-in (artist hat).
 *
 * Hosts and admins are routed away in `useEffect` based on the active hat.
 * Capability owners with multiple hats flip via the AppHeader hat switcher.
 *
 * Rhythm post-uplift: greeting → stat row → up-next card → activity feed +
 * quick actions side-by-side. Stats come from `/api/dashboard/me`.
 */
export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});

type ArtistStats = {
  ok: true;
  activeHat: 'artist';
  // AUD-006/AUD-007: confirm-readiness — mirrors the confirm gate exactly so
  // the quick action below never nudges toward something already done.
  confirmReadiness: {
    ready: boolean;
    missing: string[];
  };
  stats: {
    activeExchanges: number;
    inboxUnread: number;
    completedExchanges: number;
    openInterests: number;
  };
};

function DashboardPage() {
  const { user, activeHat, isLoading } = useAuth();
  const navigate = useNavigate();

  // Hat-based routing: /dashboard is the artist surface. If the user is
  // currently wearing a host or admin hat, send them to that surface.
  useEffect(() => {
    if (isLoading || !user) return;
    if (activeHat === 'admin') void navigate({ to: '/admin', replace: true });
    else if (activeHat === 'host') void navigate({ to: '/host/dashboard', replace: true });
  }, [user, activeHat, isLoading, navigate]);

  // Stats query. Only fires for signed-in artist hat.
  const enabled = !isLoading && user !== null && activeHat === 'artist';
  const statsQuery = useQuery<ArtistStats>({
    queryKey: ['dashboard-stats', 'artist'],
    queryFn: async () => {
      const res = await fetchOk('/api/dashboard/me', { credentials: 'include' });
      const json = (await res.json()) as ArtistStats;
      return json;
    },
    enabled,
    staleTime: 30_000,
  });

  // Fire dashboard.viewed once per stats load (per session-of-this-component).
  // Declared before any early return so hook order stays stable.
  const viewedRef = useRef<boolean>(false);
  const stats = statsQuery.data?.stats;
  useEffect(() => {
    if (stats === undefined) return;
    if (viewedRef.current) return;
    viewedRef.current = true;
    track('dashboard.viewed', {
      hat: 'artist',
      statsNonZero:
        stats.activeExchanges > 0 ||
        stats.inboxUnread > 0 ||
        stats.completedExchanges > 0 ||
        stats.openInterests > 0,
    });
  }, [stats]);

  if (!isLoading && user && activeHat !== 'artist' && activeHat !== null) {
    // Avoid flashing the artist welcome card before the redirect lands.
    return null;
  }

  const first = user ? firstName(user.displayName) : '';
  const showSkeletons = enabled && statsQuery.isLoading;

  // Greeting + status line. Voice rules: warm, specific, never performative.
  // The status line is a single specific sentence driven by the data.
  const greetingWord = resolveGreetingPhrase();
  const greetingLine = first ? `${greetingWord}, ${first}.` : `${greetingWord}.`;
  const statusLine = resolveArtistStatusLine(stats);

  // Stat tiles. inboxUnread is the action-relevant tile → accent.
  const tiles: DashboardStatTile[] = [
    {
      label: 'Active exchanges',
      value: stats?.activeExchanges ?? null,
      hint: 'Submitted or confirmed.',
      to: '/inbox?f=active',
    },
    {
      label: 'In the inbox',
      value: stats?.inboxUnread ?? null,
      hint: 'Conversations needing a reply.',
      accent: true,
      to: '/inbox?f=unread',
    },
    {
      label: 'On your road',
      value: stats?.completedExchanges ?? null,
      hint: 'Completed exchanges to date.',
      to: '/inbox?f=past',
    },
    {
      label: 'Hands raised',
      value: stats?.openInterests ?? null,
      hint: "Events you've put your hand up for.",
      to: '/events',
    },
  ];

  // Quick actions ranked by relevance. Three slots; logic colocated here so
  // it sees the same data as the stat tiles.
  const actions = resolveArtistActions(stats, statsQuery.data?.confirmReadiness);

  return (
    <AppShell tone="full">
      {/* Dashboards adopt the inbox's full-width pattern: bleed to the edges
          of the viewport with the same clamp-gutter that the AppHeader uses,
          so the stat grid, activity feed and action row get real horizontal
          breathing room on wide desktops. */}
      <Box style={{ paddingInline: 'clamp(16px, 4vw, 32px)' }}>
        <Stack gap="xl">
          <MotionSection>
            <DashboardGreeting
              eyebrow="Your road"
              greeting={greetingLine}
              lede={statusLine}
            />
          </MotionSection>

          <MotionSection delay={0.05}>
            <DashboardStatGrid
              tiles={tiles}
              loading={showSkeletons}
              onTileClick={(label) => {
                track('dashboard.stat_clicked', { tile: tileKeyFromLabel(label) });
              }}
            />
          </MotionSection>

          {!isLoading && user !== null ? (
            <MotionSection delay={0.1}>
              <UpNextCard variant="artist" />
            </MotionSection>
          ) : null}

          {enabled ? (
            <MotionSection delay={0.15}>
              <AvailabilitySummaryCard />
            </MotionSection>
          ) : null}

          {!isLoading && user !== null ? (
            <MotionSection delay={0.2}>
              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
                <Stack gap="md">
                  <SectionTitle title="Recent activity" />
                  <RecentActivityFeed />
                </Stack>
                <Stack gap="md">
                  <SectionTitle title="Where to go from here" />
                  <QuickActionRow
                    actions={actions}
                    onActionClick={(actionKey, position) => {
                      track('dashboard.quick_action_clicked', { actionKey, position });
                    }}
                  />
                </Stack>
              </SimpleGrid>
            </MotionSection>
          ) : null}

          {!isLoading && user === null ? (
            <EmptyState
              title="Sign in to see your dashboard."
              body="Create an account to send your first request."
              action={
                <Group justify="center">
                  <Button
                    component={Link}
                    to="/sign-up"
                    variant="gradient"
                    gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                  >
                    Create an account
                  </Button>
                  <Anchor component={Link} to="/sign-in">
                    Sign in
                  </Anchor>
                </Group>
              }
            />
          ) : null}
        </Stack>
      </Box>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Pure helpers — driven by the discriminated stats union.
// ---------------------------------------------------------------------------

/**
 * Resolve the single specific status line under the greeting. Priority:
 *   inbox > active > onboarding nudge.
 */
function resolveArtistStatusLine(stats: ArtistStats['stats'] | undefined): string {
  if (!stats) return 'Your exchanges, conversations, and next steps.';
  if (stats.inboxUnread > 0) {
    const noun = stats.inboxUnread === 1 ? 'message' : 'messages';
    return `${stats.inboxUnread} ${noun} waiting in your inbox.`;
  }
  if (stats.activeExchanges > 0) {
    const noun = stats.activeExchanges === 1 ? 'exchange' : 'exchanges';
    return `${stats.activeExchanges} active ${noun} on your road.`;
  }
  return 'Venues are live in your area. Browse and send your first request.';
}

function resolveArtistActions(
  stats: ArtistStats['stats'] | undefined,
  confirmReadiness: ArtistStats['confirmReadiness'] | undefined,
): QuickAction[] {
  // Default ranking when stats are still loading or empty: send → inbox →
  // profile. Same order as fully-loaded zero-state.
  const list: QuickAction[] = [];

  // AUD-006/AUD-007: not confirm-ready outranks everything else — an artist
  // who can't be confirmed yet needs to know before they invest effort
  // chasing a request a host won't be able to accept.
  if (confirmReadiness !== undefined && !confirmReadiness.ready) {
    const count = confirmReadiness.missing.length;
    list.push({
      key: 'finish-verification',
      title: 'Finish your verification',
      description: `${count} ${count === 1 ? 'detail' : 'details'} left before a host can confirm you.`,
      // /verification carries the full readiness checklist (badge trifecta +
      // identity fields) and links each item to where it's completed.
      to: '/verification',
      glyph: 'profile',
    });
  }

  if (!stats || stats.activeExchanges === 0) {
    list.push({
      key: 'send-request',
      title: 'Send your first request',
      description: "Browse what's live and write to a host.",
      to: '/browse',
      glyph: 'send',
    });
  } else {
    list.push({
      key: 'browse-more',
      title: 'Browse more venues',
      description: 'Keep an eye on rooms that fit your road.',
      to: '/browse',
      glyph: 'send',
    });
  }

  if (stats && stats.inboxUnread > 0) {
    list.push({
      key: 'open-inbox',
      title: 'Open inbox',
      description: `${stats.inboxUnread} ${stats.inboxUnread === 1 ? 'reply waiting' : 'replies waiting'}.`,
      to: '/inbox',
      glyph: 'inbox',
    });
  } else {
    list.push({
      key: 'open-inbox-quiet',
      title: 'Open inbox',
      description: "Conversations with hosts you've matched with.",
      to: '/inbox',
      glyph: 'inbox',
    });
  }

  list.push({
    key: 'plan-tour',
    title: 'Plan a tour',
    description: "Tell us where you're heading so hosts can find you.",
    to: '/tours',
    glyph: 'send',
  });

  return list.slice(0, 3);
}

function firstName(displayName: string): string {
  const trimmed = displayName.trim();
  if (trimmed.length === 0) return '';
  return trimmed.split(/\s+/)[0] ?? '';
}

/**
 * Map a stat tile label to a stable telemetry key. Keep the cardinality low
 * so the analytics dim stays cheap.
 */
function tileKeyFromLabel(label: string): string {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return slug || 'unknown';
}
